import { FlaskConical } from 'lucide-react';
import { Badge, Button, Card, type BadgeTone } from '@vnetwork/ui/primitives';
import type { JourneyNote } from '@/api/customer-journey';
import { formatDate } from '@/lib/format';
import { humanize, serviceTone } from '../../status-tones';
import { pocExpiry } from './board-helpers';

/**
 * A single POC (trial) service card inside the POC column — mirrors the legacy
 * POCServiceCard. Shows the service, its status, trial window + expiry, and the
 * three trial actions (Update result / Extend / Convert to official). The first
 * two are service-side flows still owned by the legacy app (opened in a new tab);
 * Convert runs in-app via the customer POC → official endpoint.
 */
export function PocServiceCard({
  note,
  canAct,
  converting,
  onConvert,
  onUpdateResult,
  onExtend,
}: {
  note: JourneyNote;
  canAct: boolean;
  converting: boolean;
  onConvert: (note: JourneyNote) => void;
  onUpdateResult: (note: JourneyNote) => void;
  onExtend: (note: JourneyNote) => void;
}) {
  const poc = note.service_poc;
  if (!poc) return null;
  const expiry = pocExpiry(poc.service_stopped_date);
  const expiryLabel =
    expiry.daysLeft == null
      ? null
      : expiry.status === 'expired'
        ? `Expired ${Math.abs(expiry.daysLeft)}d ago`
        : expiry.daysLeft === 0
          ? 'Due today'
          : `${expiry.daysLeft}d left`;

  return (
    <Card flat style={{ display: 'grid', gap: 'var(--vn-space-1)', padding: 'var(--vn-space-2)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-1)', minWidth: 0 }}>
        <FlaskConical size={13} style={{ color: 'var(--vn-warning-text)', flexShrink: 0 }} />
        <span
          style={{
            fontSize: 'var(--vn-font-size-xs)',
            fontWeight: 'var(--vn-font-weight-medium)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {poc.service_id} · {poc.service_name}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
        <Badge tone={serviceTone(poc.service_status) as BadgeTone} appearance="subtle" size="sm">
          {humanize(poc.service_status)}
        </Badge>
        {expiryLabel ? (
          <span
            style={{
              fontSize: 'var(--vn-font-size-xs)',
              color: expiry.isWarning ? 'var(--vn-danger-text)' : 'var(--vn-fg-muted)',
              fontWeight: expiry.isWarning ? 'var(--vn-font-weight-medium)' : undefined,
            }}
          >
            {expiryLabel}
          </span>
        ) : null}
      </div>

      {poc.service_start_date || poc.service_stopped_date ? (
        <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
          {poc.service_start_date ? formatDate(poc.service_start_date) : ''}
          {poc.service_start_date && poc.service_stopped_date ? ' → ' : ''}
          {poc.service_stopped_date ? formatDate(poc.service_stopped_date) : ''}
        </div>
      ) : null}

      {canAct ? (
        <div style={{ display: 'flex', gap: 'var(--vn-space-1)', marginTop: 'var(--vn-space-1)' }}>
          <Button size="sm" variant="secondary" onClick={() => onUpdateResult(note)} style={{ flex: 1 }}>
            Update result
          </Button>
          <Button size="sm" variant="secondary" onClick={() => onExtend(note)} style={{ flex: 1 }}>
            Extend
          </Button>
          <Button size="sm" variant="primary" loading={converting} onClick={() => onConvert(note)} style={{ flex: 1 }}>
            Convert
          </Button>
        </div>
      ) : null}
    </Card>
  );
}

export default PocServiceCard;
